import { ChangeDetectionStrategy, Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CoverageIdEnum, NewModal } from 'outshared-lib';
import { CoverageGroupToggle, CoverageInput } from '../../classes';
import { CoverageGroupIdEnum } from '../../enums';
import { CoverageGroupDataGroupInterface } from '@app/my-zone/shared/insurance-coverage/insurance-coverage-control';
import {
    WucInputCheckboxModule,
    WucInputRadioCardModule,
    WucLinkModule,
    WucPriceModule,
} from '@inshared/website-ui-components';
import { NgFor, NgIf } from '@angular/common';

@Component({
    selector: 'ins-coverage-toggle',
    templateUrl: 'coverage-toggle.component.html',
    styleUrls: ['./coverage-toggle.component.scss'],
    standalone: true,
    imports: [
        NgFor,
        NgIf,
        WucLinkModule,
        WucPriceModule,
        WucInputRadioCardModule,
        WucInputCheckboxModule,
        FormsModule
    ],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => CoverageToggleComponent),
        multi: true,
    }],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoverageToggleComponent implements ControlValueAccessor {

    @Input() public group: CoverageGroupToggle;

    public coverageIdEnum: typeof CoverageIdEnum;
    public coverageGroupIdEnum: typeof CoverageGroupIdEnum;
    public price = 0;
    public test: FormControl<boolean> = new FormControl<boolean>(true);
    public propagateChange;
    public value: { enabled: boolean, choice: string[] } = {
        enabled: false,
        choice: [],
    };

    constructor(
        private newModal: NewModal
    ) {
        this.coverageGroupIdEnum = CoverageGroupIdEnum;
        this.coverageIdEnum = CoverageIdEnum;
    }

    public writeValue(coverageIds: CoverageIdEnum[]): void {
        if (coverageIds && coverageIds.length > 0) {
            /* ngModel doesn't work well with Arrays as values, so instead, we fill this variable with the same object as the form value.
             This allows ngModel to compare the values by reference instead of value. */
            const input = this.group?.inputs?.find(
                (input) => coverageIds.every((coverageId) =>
                    input?.coverageIds.includes(coverageId)));
            this.value.choice = input?.coverageIds;
            this.price = input?.price;
            this.value.enabled = true;
        }
    }

    public registerOnChange(fn: any): void {
        this.propagateChange = () => {
            fn(this.value.choice);
        };
    }

    public registerOnTouched(): void {
        return;
    }

    public setSelected(input: CoverageInput): void {
        if (!input.acceptance) {
            return;
        }

        this.value.enabled = true;
        this.price = input.price;
        this.value.choice = input.coverageIds;
        this.propagateChange();
    }

    public toggle(): void {
        const firstInput = this.group.inputs[0];

        if (!this.value.enabled) {
            this.value.choice = [];
            this.price = 0;
        }
        else {
            this.value.choice = firstInput.coverageIds;
            this.price = firstInput.price;
        }

        this.propagateChange();
    }

    public async openModal(coverageTitle: string): Promise<void> {
        const { AdditionalCoverageInfoModalComponent } = await import('@app/shared/modals');
        this.newModal
            .open(AdditionalCoverageInfoModalComponent, {
                data: {
                    coverage_id: coverageTitle,
                    coverage_title: coverageTitle,
                },
            });
    }

    public async displayInformation(data: CoverageGroupDataGroupInterface): Promise<void> {
        const { CoverageControlInformationModal } = await import('../coverage-control-information/coverage-control-information.modal');
        this.newModal.open(CoverageControlInformationModal, { data });
    }
}
